import React from "react";
import PropTypes from "prop-types";
import { ToggleButtonGroup, ToggleButton, Tooltip } from "@mui/material";
import { LayoutGrid, List } from "lucide-react";
import { colors } from "../../../../theme";

/**
 * Default view options (Grid and Table)
 */
const DEFAULT_VIEW_OPTIONS = [
  {
    value: "grid",
    label: "Grid View",
    icon: <LayoutGrid size={18} />,
  },
  {
    value: "table",
    label: "Table View",
    icon: <List size={18} />,
  },
];

/**
 * CommonViewToggle Component
 * Reusable view switcher / toggle button group component for AutoBET.
 * Supports switching between grid and table views (or custom option sets) with uniform theme styling.
 */
const CommonViewToggle = ({
  value,
  mode,
  onChange,
  options = DEFAULT_VIEW_OPTIONS,
  size = "small",
  exclusive = true,
  disabled = false,
  tooltipArrow = true,
  children,
  sx = {},
  buttonSx = {},
  ...props
}) => {
  const activeValue = value !== undefined ? value : mode !== undefined ? mode : "grid";

  const handleChange = (event, nextValue) => {
    // In exclusive mode, avoid unselecting when clicking active item
    if (nextValue !== null && nextValue !== undefined) {
      if (typeof onChange === "function") {
        if (onChange.length === 1) {
          onChange(nextValue);
        } else {
          onChange(event, nextValue);
        }
      }
    }
  };

  return (
    <ToggleButtonGroup
      value={activeValue}
      exclusive={exclusive}
      onChange={handleChange}
      size={size}
      disabled={disabled}
      sx={{
        bgcolor: colors.slate?.[100] || "#f1f5f9",
        borderRadius: "10px",
        p: 0.3,
        border: `1px solid ${colors.dashboard?.cardBorder || "#e2e8f0"}`,
        "& .MuiToggleButton-root": {
          border: "none",
          borderRadius: "8px",
          px: 1.25,
          py: 0.75,
          color: colors.slate?.[600] || "#475569",
          transition: "all 0.2s ease-in-out",
          "&.Mui-selected": {
            bgcolor: "#ffffff",
            color: colors.primary?.main || "#6366f1",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            "&:hover": {
              bgcolor: "#ffffff",
            },
          },
          "&:hover": {
            bgcolor: "rgba(255, 255, 255, 0.5)",
          },
          ...buttonSx,
        },
        ...sx,
      }}
      {...props}
    >
      {children
        ? children
        : options.map((opt) => {
            const label = opt.label || opt.title || opt.tooltip || opt.value;
            const ariaLabel = opt.ariaLabel || label;
            const tooltipTitle = opt.tooltip || label;

            return (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                aria-label={ariaLabel}
                disabled={opt.disabled}
              >
                {tooltipTitle ? (
                  <Tooltip title={tooltipTitle} arrow={tooltipArrow}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      {opt.icon}
                      {opt.text && <span style={{ marginLeft: 6 }}>{opt.text}</span>}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {opt.icon}
                    {opt.text && <span style={{ marginLeft: 6 }}>{opt.text}</span>}
                  </span>
                )}
              </ToggleButton>
            );
          })}
    </ToggleButtonGroup>
  );
};

CommonViewToggle.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.node,
      tooltip: PropTypes.node,
      ariaLabel: PropTypes.string,
      icon: PropTypes.node,
      text: PropTypes.node,
      disabled: PropTypes.bool,
    })
  ),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  exclusive: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltipArrow: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
  buttonSx: PropTypes.object,
};

export {
  CommonViewToggle,
  CommonViewToggle as CommonViewSwitcher,
  CommonViewToggle as CommonViewModeToggle,
  CommonViewToggle as CommonToggleButtonGroup,
};

export default CommonViewToggle;
